import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { AppFooter } from "@/components/layout/AppFooter";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { toast } from "sonner";
import { Mail, KeyRound } from "lucide-react";
import { motion } from "framer-motion";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot", { email });
      setSent(true);
    } catch {
      toast.error("Could not send reset email");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="relative flex-1 flex items-center overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-[420px] h-[420px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(255,180,0,0.30), transparent 60%)" }} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
          className="relative w-full max-w-md mx-auto px-6 py-12"
        >
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-3">
            <KeyRound size={12} /> Forgot password
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Reset your password.</h1>
          <p className="mt-2 text-neutral-600 text-[15px]">We'll email you a one-hour link to choose a new one.</p>

          {sent ? (
            <div className="bg-white border border-black/8 rounded-2xl mt-8 p-8 text-center shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)]">
              <Mail size={32} className="mx-auto text-[#FFB400]" />
              <div className="mt-3 font-display font-bold text-xl">Check your inbox</div>
              <p className="text-sm text-neutral-600 mt-2">If an account exists for <strong>{email}</strong>, a reset link is on its way.</p>
              <Link to="/login" className="mt-5 inline-block text-sm font-semibold text-[#0A0C10] underline underline-offset-4 decoration-[#FFB400]/60 hover:text-[#FFB400] transition-colors">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-black/8 rounded-2xl mt-8 p-6 sm:p-8 space-y-4 shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)]">
              <GlowField label="Your account email" testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.com" />
              <CtaButton type="submit" variant="primary" size="lg" disabled={loading} className="w-full rounded-full" data-testid="forgot-submit">
                {loading ? "Sending…" : "Email me a reset link"}
              </CtaButton>
              <div className="text-center text-sm text-neutral-600">
                Remembered it? <Link to="/login" className="font-semibold text-[#0A0C10] underline underline-offset-4 decoration-[#FFB400]/50 hover:text-[#FFB400] transition-colors">Sign in</Link>
              </div>
            </form>
          )}
        </motion.div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}
